import { GoogleGenerativeAI } from "@google/generative-ai";
import { Dialog } from "@headlessui/react";
import CryptoJS from "crypto-js";
import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	updateDoc,
} from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import Joyride from "react-joyride";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { db } from "../firebaseConfig";

const Dashboard = () => {
	const { user, encryptionKey } = useUser();
	const [entriesCount, setEntriesCount] = useState(0);
	const [sessionsCount, setSessionsCount] = useState(0);
	const [longestStreak, setLongestStreak] = useState(0);
	const [topics, setTopics] = useState([]);
	const [markedDates, setMarkedDates] = useState([]);
	const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
	const [isLoadingTopics, setIsLoadingTopics] = useState(true);
	const navigate = useNavigate();
	const [currentStreak, setCurrentStreak] = useState(0);
	const [runTour, setRunTour] = useState(false);
	const [showEmailPrefs, setShowEmailPrefs] = useState(false);
	const [selectedPrefs, setSelectedPrefs] = useState([
		"weekly",
		"educational",
	]);
	const [bookmarkedSuggestions, setBookmarkedSuggestions] = useState([]);
	const [selectedTopicIndex, setSelectedTopicIndex] = useState(null);

	const DashboardSteps = [
		{
			target: ".dashboard-section",
			content:
				"This is your handy dashboard. This will greet you every time you log in.",
			placement: "center",
		},
		{
			target: ".statistics-section",
			content:
				"Track your journey through journal entries, therapy sessions, and activity streaks",
			placement: "right",
		},
		{
			target: ".calendar-section",
			content:
				"View your activity history. Blue dots indicate days with entries or sessions. Let's build a streak!",
			placement: "left",
		},
		{
			target: ".topics-section",
			content:
				"Get personalized topics to discuss based on your entries and sessions",
			placement: "top",
		},
		{
			target: ".bookmarks-section",
			content:
				"Save important suggestions and insights from your entries and sessions for quick reference",
			placement: "top",
		},
		{
			target: ".dashboard-section",
			content:
				"Before you ask: All your data is securely end to end encrypted. It's only visible to you!",
			placement: "center",
		},
	];

	useEffect(() => {
		const fetchData = async () => {
			if (!user || !encryptionKey) return;

			// Check dashboard demo status
			const userRef = doc(db, "Users", user.uid);
			const userDoc = await getDoc(userRef);
			if (userDoc.exists()) {
				const userData = userDoc.data();
				if (!userData.dashboardDemo) {
					setRunTour(true);
					await updateDoc(userRef, { dashboardDemo: true });
				} else if (!userData.emailPrefsSet) {
					setShowEmailPrefs(true);
				}
			}

			// Fetch both journal entries and therapy sessions
			const entriesCollection = collection(
				db,
				"Users",
				user.uid,
				"entries"
			);
			const sessionsCollection = collection(
				db,
				"Users",
				user.uid,
				"sessions"
			);

			const [entriesSnapshot, sessionsSnapshot] = await Promise.all([
				getDocs(entriesCollection),
				getDocs(sessionsCollection),
			]);

			setEntriesCount(entriesSnapshot.size);
			setSessionsCount(sessionsSnapshot.size);

			// Combine and process all activities
			const activities = [
				...entriesSnapshot.docs.map((doc) => ({
					...doc.data(),
					date: doc.data().createdAt?.toDate(),
					type: "journal",
				})),
				...sessionsSnapshot.docs.map((doc) => ({
					...doc.data(),
					date: doc.data().createdAt?.toDate(),
					type: "therapy",
				})),
			];

			if (activities.length > 0) {
				// Set marked dates for the calendar
				const activityDates = activities
					.filter((activity) => activity.date)
					.map((activity) => activity.date.toDateString());
				setMarkedDates([...new Set(activityDates)]);

				// Sort all activities by date
				const sortedActivities = activities
					.filter((activity) => activity.date)
					.sort((a, b) => b.date - a.date);

				// Calculate longest streak considering both types of activities
				let currentStreak = 1;
				let maxStreak = 1;

				// Group activities by date (to handle multiple activities on same day)
				const activitiesByDate = sortedActivities.reduce(
					(acc, activity) => {
						const dateStr = activity.date.toDateString();
						if (!acc[dateStr]) {
							acc[dateStr] = [];
						}
						acc[dateStr].push(activity);
						return acc;
					},
					{}
				);

				// Convert to array of dates for streak calculation
				const uniqueDates = Object.keys(activitiesByDate)
					.map((dateStr) => new Date(dateStr))
					.sort((a, b) => b - a);

				for (let i = 1; i < uniqueDates.length; i++) {
					const prevDate = uniqueDates[i - 1];
					const currDate = uniqueDates[i];

					// Check if activities are on consecutive days
					const diffDays = Math.floor(
						(prevDate - currDate) / (1000 * 60 * 60 * 24)
					);

					if (diffDays === 1) {
						currentStreak++;
						maxStreak = Math.max(maxStreak, currentStreak);
					} else {
						currentStreak = 1;
					}
				}

				// Calculate current streak
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				const yesterday = new Date(today);
				yesterday.setDate(yesterday.getDate() - 1);

				// Get the most recent activity date
				const mostRecentDate = uniqueDates[0];
				let currentStreakCount = 1;

				// Check if the most recent activity was today or yesterday
				if (mostRecentDate >= today || mostRecentDate >= yesterday) {
					// Count backwards from most recent date
					for (let i = 1; i < uniqueDates.length; i++) {
						const prevDate = uniqueDates[i - 1];
						const currDate = uniqueDates[i];

						const diffDays = Math.floor(
							(prevDate - currDate) / (1000 * 60 * 60 * 24)
						);

						if (diffDays === 1) {
							currentStreakCount++;
						} else {
							break;
						}
					}
					setCurrentStreak(currentStreakCount);
				} else {
					setCurrentStreak(0); // Reset streak if no activity yesterday or today
				}

				setLongestStreak(maxStreak);
			}

			// Decrypt and process entries and sessions for AI
			let journalHistory = "";
			entriesSnapshot.docs.forEach((doc) => {
				const data = doc.data();
				const decryptedTitle = CryptoJS.AES.decrypt(
					data.title,
					encryptionKey
				).toString(CryptoJS.enc.Utf8);
				const decryptedBody = CryptoJS.AES.decrypt(
					data.body,
					encryptionKey
				).toString(CryptoJS.enc.Utf8);
				journalHistory += `Title: ${decryptedTitle}\nEntry: ${decryptedBody}\n\n`;
			});

			let conversationHistory = "";
			sessionsSnapshot.docs.forEach((doc) => {
				const data = doc.data();
				const messageKeys = Object.keys(data)
					.filter((key) => key.startsWith("message_"))
					.sort();

				messageKeys.forEach((key) => {
					const decryptedMessage = CryptoJS.AES.decrypt(
						data[key],
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					conversationHistory += `${decryptedMessage}\n`;
				});
			});

			// Set loading to true before generating topics
			setIsLoadingTopics(true);

			if (
				journalHistory.length === 0 ||
				conversationHistory.length === 0
			) {
				setTopics([
					"How are you feeling today?",
					"What is occupying your mind recently?",
					"What are your short term goals?",
				]);
				setIsLoadingTopics(false);
				return;
			}

			// Generate topics using Gemini
			try {
				const model = genAI.getGenerativeModel({
					model: "gemini-1.5-flash",
				});
				const prompt = `Based on these therapy conversations and journal entries, suggest 3 specific, thoughtful questions for the user to explore further. Make them personal and relevant to their experiences, but extremely concise:

				Journal entries: ${journalHistory}
				Therapy conversations: ${conversationHistory}

				Format the response as a semicolon delimited string with 3 questions, no headers. Ensure that the questions are very short and don't contain any special characters. Ensure that semicolons are not used in the questions but are used as delimiters. Make the questions in first person, such as "What is occupying my mind recently?"`;

				const result = await model.generateContent(prompt);
				const response = result.response.text();
				console.log("aniket", response);
				// Parse CSV string into array
				const newTopics = response
					.split(";")
					.map((topic) => topic.trim());
				setTopics(newTopics);
			} catch (error) {
				console.error("Error generating topics:", error);
				setIsLoadingTopics(false);
				setTopics([
					"How am I feeling today?",
					"What is occupying my mind recently?",
					"What are my short term goals?",
				]);
			} finally {
				setIsLoadingTopics(false);
			}
		};

		fetchData();
	}, [user, encryptionKey]);

	useEffect(() => {
		const fetchBookmarks = async () => {
			if (!user || !encryptionKey) return;

			try {
				const bookmarksRef = collection(
					db,
					"Users",
					user.uid,
					"bookmarks"
				);
				const bookmarksSnapshot = await getDocs(bookmarksRef);
				const bookmarks = [];

				bookmarksSnapshot.forEach((doc) => {
					const decryptedSuggestion = CryptoJS.AES.decrypt(
						doc.data().suggestion,
						encryptionKey
					).toString(CryptoJS.enc.Utf8);
					bookmarks.push(decryptedSuggestion);
				});

				setBookmarkedSuggestions(bookmarks);
			} catch (error) {
				console.error("Error fetching bookmarks:", error);
			}
		};

		fetchBookmarks();
	}, [user, encryptionKey]);

	// Add these calendar helper functions
	const getDaysInMonth = (date) => {
		return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
	};

	const getFirstDayOfMonth = (date) => {
		return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
	};

	// Custom calendar component
	const CustomCalendar = () => {
		const currentDate = new Date();
		const daysInMonth = getDaysInMonth(currentDate);
		const firstDayOfMonth = getFirstDayOfMonth(currentDate);
		const monthName = currentDate.toLocaleString("default", {
			month: "long",
		});
		const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

		return (
			<div className="w-full max-w-md mx-auto">
				<div className="text-lg text-center font-light mb-3">
					{monthName} Activity
				</div>
				<div className="grid grid-cols-7 gap-3">
					{/* Week days - Updated className to match calendar days width */}
					{weekDays.map((day) => (
						<div
							key={day}
							className="w-7 text-center text-base text-gray-500 font-light">
							{day[0]}
						</div>
					))}

					{/* Empty cells for days before the first day of month */}
					{Array(firstDayOfMonth)
						.fill(null)
						.map((_, index) => (
							<div key={`empty-${index}`} className="h-6" />
						))}

					{/* Calendar days */}
					{Array(daysInMonth)
						.fill(null)
						.map((_, index) => {
							const day = index + 1;
							const dateStr = new Date(
								currentDate.getFullYear(),
								currentDate.getMonth(),
								day
							).toDateString();
							const isMarked = markedDates.includes(dateStr);

							return (
								<div
									key={day}
									className={`w-6 h-6 flex items-center justify-center rounded-full
									${isMarked ? "bg-blue-400 text-white" : "text-gray-700"}`}>
									{day}
								</div>
							);
						})}
				</div>
			</div>
		);
	};

	const handleEmailPreferences = async () => {
		const userRef = doc(db, "Users", user.uid);
		await updateDoc(userRef, {
			emailPreferences: selectedPrefs,
			emailPrefsSet: true,
		});
		setShowEmailPrefs(false);
	};

	const handleDeleteBookmark = async (suggestionToDelete, index) => {
		if (!user || !encryptionKey) return;

		try {
			const bookmarksRef = collection(db, "Users", user.uid, "bookmarks");
			const bookmarksSnapshot = await getDocs(bookmarksRef);

			// Find and delete the matching bookmark
			for (const doc of bookmarksSnapshot.docs) {
				const decryptedSuggestion = CryptoJS.AES.decrypt(
					doc.data().suggestion,
					encryptionKey
				).toString(CryptoJS.enc.Utf8);

				if (decryptedSuggestion === suggestionToDelete) {
					await deleteDoc(doc.ref);
					// Update local state
					setBookmarkedSuggestions((prevBookmarks) =>
						prevBookmarks.filter((_, i) => i !== index)
					);
					break;
				}
			}
		} catch (error) {
			console.error("Error deleting bookmark:", error);
		}
	};

	const handleTopicClick = (index) => {
		setSelectedTopicIndex(selectedTopicIndex === index ? null : index);
	};

	const startNewJournalEntry = async (topic) => {
		try {
			if (!user) return;

			const newEntry = {
				createdAt: new Date(),
				title: CryptoJS.AES.encrypt(topic, encryptionKey).toString(),
				body: CryptoJS.AES.encrypt("", encryptionKey).toString(),
			};

			const docRef = await addDoc(
				collection(db, "Users", user.uid, "entries"),
				newEntry
			);

			navigate(`/journal/${docRef.id}`);
		} catch (error) {
			console.error("Error creating new journal entry:", error);
		}
	};

	const startNewTherapySession = (topic) => {
		// Instead of using sessionStorage, use URL parameters
		navigate("/therapy", {
			state: {
				initialMessage: `Hi, today I want to talk about the topic "${topic.toLowerCase()}"`,
			},
		});
	};

	return (
		<>
			<div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 dashboard-section">
				<div className="w-full max-w-6xl backdrop-blur-lg rounded-3xl shadow-xl overflow-auto bg-white bg-opacity-90">
					<div className="p-6 flex flex-col h-full">
						<div className="flex justify-center items-center mb-3 py-3">
							<div className="w-10 md:w-12 mr-5">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 200 250"
									className="w-full">
									<rect
										x="90"
										y="170"
										width="20"
										height="80"
										fill="#1e3a8a"
									/>
									<polygon
										points="10,170 190,170 100,50"
										fill="#3b82f6"
									/>
									<polygon
										points="20,120 180,120 100,20"
										fill="#60a5fa"
									/>
									<polygon
										points="30,70 170,70 100,0"
										fill="#93c5fd"
									/>
								</svg>
							</div>
							<h1 className="text-3xl font-light text-gray-800">
								Dashboard
							</h1>
						</div>

						{/* Stats and Calendar Row */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
							{/* Stats Section */}
							<div className="statistics-section bg-white rounded-xl shadow-sm border border-gray-100 p-4">
								<h2 className="text-xl font-light text-gray-800 mb-4 text-center">
									Reflection Statistics
								</h2>
								<div className="space-y-1">
									{[
										{
											label: "Journal Entries",
											value: entriesCount,
										},
										{
											label: "Therapy Sessions",
											value: sessionsCount,
										},
										{
											label: "Current Streak",
											value: `${currentStreak} days`,
										},
										{
											label: "Longest Streak",
											value: `${longestStreak} days`,
										},
									].map((stat, index) => (
										<div
											key={index}
											className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
											<span className="text-gray-600">
												{stat.label}
											</span>
											<span className="text-blue-600 font-light">
												{stat.value}
											</span>
										</div>
									))}
								</div>
							</div>

							{/* Calendar Section - Moved up */}
							<div className="calendar-section bg-white rounded-xl shadow-sm border border-gray-100 p-4">
								<CustomCalendar />
							</div>
						</div>

						{/* Topics and Bookmarks Section */}
						<div className="mt-auto flex justify-center">
							<div className="grid grid-cols-1 md:grid-cols-10 gap-6 w-full">
								{/* Topics Section */}
								<div className="col-span-1 md:col-span-4 topics-section bg-white p-4 rounded-xl shadow-sm border border-gray-100">
									<h2 className="text-xl font-light text-gray-800 mb-4 text-center">
										New Topics to Explore
									</h2>
									{isLoadingTopics ? (
										<div className="flex justify-center items-center h-32">
											<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
										</div>
									) : (
										<div className="space-y-2 max-h-[25vh] overflow-y-auto pr-2">
											{topics.map((topic, index) => (
												<motion.div
													key={index}
													layout
													className={`p-2 text-sm rounded-lg cursor-pointer transition-all duration-300 
														${
															selectedTopicIndex ===
															index
																? "bg-white"
																: "bg-blue-400 text-white hover:bg-blue-600"
														}`}>
													<AnimatePresence mode="wait">
														{selectedTopicIndex ===
														index ? (
															<motion.div
																initial={{
																	opacity: 0,
																	y: -10,
																}}
																animate={{
																	opacity: 1,
																	y: 0,
																}}
																exit={{
																	opacity: 0,
																	y: 10,
																}}
																transition={{
																	duration: 0.2,
																}}
																className="flex flex-col space-y-2">
																<div>
																	{topic}
																</div>
																<div className="flex space-x-2">
																	<motion.button
																		whileHover={{
																			scale: 1.05,
																		}}
																		whileTap={{
																			scale: 0.95,
																		}}
																		onClick={() =>
																			startNewTherapySession(
																				topic
																			)
																		}
																		className="flex-1 px-3 py-1 bg-blue-400 text-white rounded-lg hover:bg-blue-600 transition-colors">
																		Talk
																	</motion.button>
																	<motion.button
																		whileHover={{
																			scale: 1.05,
																		}}
																		whileTap={{
																			scale: 0.95,
																		}}
																		onClick={() =>
																			startNewJournalEntry(
																				topic
																			)
																		}
																		className="flex-1 px-3 py-1 bg-blue-400 text-white rounded-lg hover:bg-blue-600 transition-colors">
																		Write
																	</motion.button>
																</div>
															</motion.div>
														) : (
															<motion.div
																initial={{
																	opacity: 0,
																	y: 10,
																}}
																animate={{
																	opacity: 1,
																	y: 0,
																}}
																exit={{
																	opacity: 0,
																	y: -10,
																}}
																transition={{
																	duration: 0.2,
																}}
																onClick={() =>
																	handleTopicClick(
																		index
																	)
																}>
																{topic}
															</motion.div>
														)}
													</AnimatePresence>
												</motion.div>
											))}
										</div>
									)}
								</div>

								{/* Bookmarks Section */}
								<div className="col-span-1 md:col-span-6 bookmarks-section bg-white p-4 rounded-xl shadow-sm border border-gray-100">
									<h2 className="text-xl font-light text-gray-800 mb-4 text-center">
										Saved Bookmarks
									</h2>
									<div className="space-y-2 max-h-[25vh] overflow-y-auto pr-2">
										{bookmarkedSuggestions.length === 0 ? (
											<div className="text-center text-gray-500 italic">
												No bookmarks yet. All your saved
												suggestions from the entries and
												sessions will pop up here!
											</div>
										) : (
											bookmarkedSuggestions.map(
												(suggestion, index) => (
													<div
														key={index}
														className="p-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-all duration-300 relative group">
														{suggestion}
														<button
															onClick={() =>
																handleDeleteBookmark(
																	suggestion,
																	index
																)
															}
															className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 hover:text-red-600"
															aria-label="Delete bookmark">
															<svg
																xmlns="http://www.w3.org/2000/svg"
																className="h-4 w-4"
																fill="none"
																viewBox="0 0 24 24"
																stroke="currentColor">
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth={
																		2
																	}
																	d="M6 18L18 6M6 6l12 12"
																/>
															</svg>
														</button>
													</div>
												)
											)
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Joyride
				steps={DashboardSteps}
				run={runTour}
				continuous={true}
				showSkipButton={true}
				showProgress={false}
				styles={{
					options: {
						primaryColor: "#60a5fa",
						textColor: "#374151",
					},
					buttonClose: {
						display: "none",
					},
				}}
				locale={{
					last: "Done!",
				}}
			/>

			{/* Add Email Preferences Dialog */}
			<Dialog
				open={showEmailPrefs}
				onClose={() => {}}
				className="relative z-50">
				<div className="fixed inset-0 bg-black/30" aria-hidden="true" />

				<div className="fixed inset-0 flex items-center justify-center p-4">
					<Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-4">
						<Dialog.Title className="text-xl font-medium mb-4">
							Choose Your Email Preferences. You can always change
							them in settings.
						</Dialog.Title>
						<div className="space-y-4">
							{[
								{
									id: "weekly",
									title: "Weekly Updates",
									description:
										"Get personalized summaries of your progress and reflection prompts to continue your growth.",
								},
								{
									id: "educational",
									title: "Educational and Product Content",
									description:
										"Receive expert tips, wellness strategies, and updates about new features and improvements.",
								},
							].map((pref) => (
								<div
									key={pref.id}
									className="flex items-start space-x-3">
									<input
										type="checkbox"
										id={pref.id}
										checked={selectedPrefs.includes(
											pref.id
										)}
										onChange={(e) => {
											if (e.target.checked) {
												setSelectedPrefs([
													...selectedPrefs,
													pref.id,
												]);
											} else {
												setSelectedPrefs(
													selectedPrefs.filter(
														(p) => p !== pref.id
													)
												);
											}
										}}
										className="mt-1"
									/>
									<label
										htmlFor={pref.id}
										className="flex flex-col">
										<span className="font-medium">
											{pref.title}
										</span>
										<span className="text-sm text-gray-500">
											{pref.description}
										</span>
									</label>
								</div>
							))}

							<div className="pt-4 flex justify-end space-x-3">
								<button
									onClick={() => {
										handleEmailPreferences();
									}}
									className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
									{selectedPrefs.length > 0
										? "Save Preferences"
										: "No, thank you"}
								</button>
							</div>
						</div>
					</Dialog.Panel>
				</div>
			</Dialog>
		</>
	);
};

export default Dashboard;
